/* ==========================================================================
   LUV STOREFRONT — BESPOKE AGENCY MOTION & INTERACTION ENGINE
   Optimized for 60-120fps hardware acceleration ("Smoothness > Features")
   Features:
   - Lenis smooth scroll (instant, snappy & silky lerp)
   - Bioavailability Truth Lab (interactive 3-tab engine)
   - Duo Pack Selector with live tier pricing
   - Slide-out Ajax Cart Drawer with free-shipping threshold bar
   - Smart IntersectionObserver video auto-play & audio controls
   - Interactive Pancake Flip test
   - Periodic & interactive winking mascot logo badge
   - Sticky buy bar with IntersectionObserver
   ========================================================================== */

(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let lenisInstance = null;

  /* ------------------------------------------------------------------------
     1. LENIS SMOOTH SCROLL INITIALIZATION
     ------------------------------------------------------------------------ */
  function initLenis() {
    if (reducedMotion || typeof Lenis === 'undefined') return;

    lenisInstance = new Lenis({
      lerp: 0.12,
      wheelMultiplier: 1.0,
      touchMultiplier: 1.2,
      smoothWheel: true,
      smoothTouch: false
    });

    function raf(time) {
      lenisInstance.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    // Smooth anchor navigation
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const targetId = this.getAttribute('href');
        if (!targetId || targetId === '#') return;
        const targetEl = document.querySelector(targetId);
        if (!targetEl) return;

        e.preventDefault();
        const header = document.querySelector('.luv-header-wrapper');
        const offset = header ? -header.offsetHeight : 0;
        if (lenisInstance) {
          lenisInstance.scrollTo(targetEl, { offset });
        } else {
          targetEl.scrollIntoView({ behavior: 'smooth' });
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     5. BIOAVAILABILITY TRUTH LAB (INTERACTIVE TABBED ENGINE)
     ------------------------------------------------------------------------ */
  function initTruthMachine() {
    const tabs = document.querySelectorAll('.luv-truth__tab-btn');
    if (!tabs.length) return;

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const targetId = tab.getAttribute('data-truth-tab');
        
        // Update active tab button
        tabs.forEach(t => {
          t.classList.remove('is-active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('is-active');
        tab.setAttribute('aria-selected', 'true');

        // Update active panel
        const panels = document.querySelectorAll('.luv-truth__panel');
        panels.forEach(p => {
          p.classList.remove('is-active');
          p.hidden = true;
        });

        const activePanel = document.getElementById(`truth-panel-${targetId}`);
        if (activePanel) {
          activePanel.classList.add('is-active');
          activePanel.hidden = false;
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     6. PACK BUNDLE SELECTOR (DUO SHOWCASE & PDP)
     ------------------------------------------------------------------------ */
  function initDuoPackSelector() {
    const packContainers = document.querySelectorAll('[data-pack-group]');
    packContainers.forEach(group => {
      const options = group.querySelectorAll('.luv-pack-option');
      const card = group.closest('.luv-duo-card');
      const pdpBuybox = group.closest('.luv-pdp__buybox');
      const atcButton = card?.querySelector('.btn-add-to-cart') || pdpBuybox?.querySelector('.btn-add-to-cart');
      const stickyAtcButton = document.querySelector('.sticky-atc-btn');
      const stickyPrice = document.getElementById('sticky-price-display');

      options.forEach(opt => {
        opt.addEventListener('click', () => {
          options.forEach(o => o.classList.remove('is-selected'));
          opt.classList.add('is-selected');

          const radio = opt.querySelector('input[type="radio"]');
          if (radio) radio.checked = true;

          const qty = opt.getAttribute('data-qty') || '1';
          const price = opt.getAttribute('data-price') || '';

          if (atcButton) {
            atcButton.setAttribute('data-quantity', qty);
            const priceSpan = atcButton.querySelector('.btn-atc-price');
            if (priceSpan && price) priceSpan.textContent = price;
          }

          if (stickyAtcButton) {
            stickyAtcButton.setAttribute('data-quantity', qty);
          }
          if (stickyPrice && price) {
            stickyPrice.textContent = price;
          }
        });
      });
    });
  }

  /* ------------------------------------------------------------------------
     7. VIDEO AUTOPLAY & SOUND CONTROLLER
     ------------------------------------------------------------------------ */
  function initVideoControls() {
    const videos = document.querySelectorAll('video[data-smart-video], video[data-ugc-video]');
    if ('IntersectionObserver' in window) {
      const videoObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.isIntersecting) {
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch(() => {});
            }
          } else {
            video.pause();
          }
        });
      }, { rootMargin: '100px 0px 100px 0px', threshold: 0.1 });

      videos.forEach(v => videoObserver.observe(v));
    }

    // Sound toggle buttons
    document.querySelectorAll('[data-sound-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const targetId = btn.getAttribute('data-sound-toggle');
        const video = document.getElementById(targetId);
        if (!video) return;

        video.muted = !video.muted;
        const icon = btn.querySelector('.sound-icon');
        const label = btn.querySelector('.sound-label');

        if (video.muted) {
          if (icon) icon.textContent = '🔇';
          if (label) label.textContent = 'Unmute';
        } else {
          if (icon) icon.textContent = '🔊';
          if (label) label.textContent = 'Mute';
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     8. SLIDE-OUT AJAX CART DRAWER ENGINE
     ------------------------------------------------------------------------ */
  const CartDrawer = {
    drawerEl: null,
    overlayEl: null,
    itemsContainer: null,
    subtotalEl: null,
    meterTextEl: null,
    meterFillEl: null,
    threshold: 45,

    init() {
      this.drawerEl = document.querySelector('.luv-cart-drawer');
      this.overlayEl = document.querySelector('.luv-cart-drawer-overlay');
      this.itemsContainer = document.querySelector('.luv-cart-drawer__items');
      this.subtotalEl = document.querySelector('.luv-cart-subtotal-val');
      this.meterTextEl = document.querySelector('.luv-cart-shipping-text');
      this.meterFillEl = document.querySelector('.luv-cart-shipping-fill');

      if (!this.drawerEl) return;

      // Close handlers
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-close]')) {
          this.close();
        }
      });
      if (this.overlayEl) {
        this.overlayEl.addEventListener('click', () => this.close());
      }
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });

      // Cart toggle in header
      document.querySelectorAll('[data-cart-open]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Add to Cart intercepts
      document.addEventListener('click', (e) => {
        const atc = e.target.closest('.btn-add-to-cart');
        if (!atc) return;
        e.preventDefault();

        const variantId = atc.getAttribute('data-variant-id');
        const qty = parseInt(atc.getAttribute('data-quantity') || '1', 10);
        if (variantId) {
          this.addItem(variantId, qty, atc);
        }
      });

      // Bundle Add intercepts (e.g. Variety Starter Duo)
      document.addEventListener('click', (e) => {
        const bundleBtn = e.target.closest('.btn-add-bundle');
        if (!bundleBtn) return;
        e.preventDefault();

        const v1 = bundleBtn.getAttribute('data-variant-1');
        const v2 = bundleBtn.getAttribute('data-variant-2');
        if (v1 && v2) {
          this.addBundle([
            { id: v1, quantity: 1 },
            { id: v2, quantity: 1 }
          ], bundleBtn);
        }
      });

      // Initial fetch to populate if needed
      this.refresh();
    },

    isOpen() {
      return this.drawerEl && this.drawerEl.classList.contains('is-open');
    },

    open() {
      if (!this.drawerEl) return;
      this.drawerEl.classList.add('is-open');
      if (this.overlayEl) this.overlayEl.classList.add('is-open');
      document.body.classList.add('cart-open');
      document.documentElement.classList.add('cart-open');
      if (lenisInstance) lenisInstance.stop();
      this.refresh();
    },

    close() {
      if (!this.drawerEl) return;
      this.drawerEl.classList.remove('is-open');
      if (this.overlayEl) this.overlayEl.classList.remove('is-open');
      document.body.classList.remove('cart-open');
      document.documentElement.classList.remove('cart-open');
      if (lenisInstance) lenisInstance.start();
    },

    addItem(variantId, quantity, buttonEl) {
      const origText = buttonEl ? buttonEl.innerHTML : '';
      if (buttonEl) {
        buttonEl.disabled = true;
        buttonEl.innerHTML = '<span>Adding to Stack...</span>';
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      })
      .then(res => res.json())
      .then(item => {
        if (buttonEl) {
          buttonEl.innerHTML = '<span>✓ Added!</span>';
          setTimeout(() => {
            buttonEl.disabled = false;
            buttonEl.innerHTML = origText;
          }, 1200);
        }
        this.open();
      })
      .catch(err => {
        console.error('Error adding to cart:', err);
        if (buttonEl) {
          buttonEl.disabled = false;
          buttonEl.innerHTML = origText;
        }
      });
    },

    addBundle(items, buttonEl) {
      const origText = buttonEl ? buttonEl.innerHTML : '';
      if (buttonEl) {
        buttonEl.disabled = true;
        buttonEl.innerHTML = '<span>Adding Variety Duo...</span>';
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items })
      })
      .then(res => res.json())
      .then(() => {
        if (buttonEl) {
          buttonEl.innerHTML = '<span>✓ Variety Duo Added!</span>';
          setTimeout(() => {
            buttonEl.disabled = false;
            buttonEl.innerHTML = origText;
          }, 1200);
        }
        this.open();
      })
      .catch(err => {
        console.error('Error adding bundle to cart:', err);
        if (buttonEl) {
          buttonEl.disabled = false;
          buttonEl.innerHTML = origText;
        }
      });
    },

    updateQty(lineKey, quantity) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity })
      })
      .then(res => res.json())
      .then(cart => this.renderCart(cart))
      .catch(err => console.error('Error updating cart:', err));
    },

    refresh() {
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => this.renderCart(cart))
        .catch(err => console.error('Error fetching cart:', err));
    },

    renderCart(cart) {
      // Update header badges
      document.querySelectorAll('.luv-header__cart-count').forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });

      if (!this.itemsContainer) return;

      if (cart.items.length === 0) {
        this.itemsContainer.innerHTML = `
          <div style="text-align: center; padding-block: 3rem;">
            <div style="font-size: 3rem; margin-bottom: 1rem;">🥞</div>
            <p style="font-family: var(--font-display); font-size: var(--text-lg); font-weight: 800;">Your griddle is empty!</p>
            <p style="color: var(--luv-text-muted); font-size: var(--text-sm); margin-top: 0.5rem; margin-bottom: 1.5rem;">Pick your high-protein mix to start flipping.</p>
            <a href="#pick-your-mix" class="btn-luv btn-luv--primary" data-cart-close>Shop The Mixes</a>
          </div>
        `;
        if (this.subtotalEl) this.subtotalEl.textContent = '$0.00';
        if (this.meterTextEl) this.meterTextEl.textContent = `Add $${this.threshold}.00 for FREE SHIPPING!`;
        if (this.meterFillEl) this.meterFillEl.style.width = '0%';
        return;
      }

      // Render Line Items
      let html = '';
      cart.items.forEach(item => {
        const itemPrice = (item.final_line_price / 100).toFixed(2);
        html += `
          <div class="luv-cart-item" data-line-key="${item.key}">
            <div class="luv-cart-item__thumb">
              <img src="${item.image || ''}" alt="${item.title}">
            </div>
            <div>
              <div class="luv-cart-item__title">${item.product_title}</div>
              <div style="font-size: 0.75rem; color: var(--luv-text-muted);">${item.variant_title !== 'Default Title' ? item.variant_title : ''}</div>
              <div class="luv-cart-item__price">$${itemPrice}</div>
              <div class="luv-cart-qty">
                <button type="button" class="btn-qty-dec" data-key="${item.key}" data-qty="${item.quantity - 1}">−</button>
                <span>${item.quantity}</span>
                <button type="button" class="btn-qty-inc" data-key="${item.key}" data-qty="${item.quantity + 1}">+</button>
              </div>
            </div>
            <div>
              <button type="button" class="btn-remove-item" data-key="${item.key}" style="color: var(--luv-text-muted); font-size: 1.2rem; padding: 0.25rem;">×</button>
            </div>
          </div>
        `;
      });

      this.itemsContainer.innerHTML = html;

      // Bind Quantity & Remove handlers
      this.itemsContainer.querySelectorAll('.btn-qty-dec, .btn-qty-inc, .btn-remove-item').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const key = btn.getAttribute('data-key');
          const qty = parseInt(btn.getAttribute('data-qty') || '0', 10);
          this.updateQty(key, qty);
        });
      });

      // Update Subtotal
      const subtotal = (cart.total_price / 100).toFixed(2);
      if (this.subtotalEl) this.subtotalEl.textContent = `$${subtotal}`;

      // Free Shipping Meter Calculation
      const currentTotal = cart.total_price / 100;
      const remaining = (this.threshold - currentTotal).toFixed(2);
      if (currentTotal >= this.threshold) {
        if (this.meterTextEl) this.meterTextEl.innerHTML = '🎉 <span style="color: var(--luv-charcoal); font-weight: 900;">YOU UNLOCKED FREE SHIPPING!</span>';
        if (this.meterFillEl) {
          this.meterFillEl.style.width = '100%';
          this.meterFillEl.style.background = 'var(--luv-blue)';
        }
      } else {
        const percent = Math.min((currentTotal / this.threshold) * 100, 100);
        if (this.meterTextEl) this.meterTextEl.textContent = `Add $${remaining} more for FREE SHIPPING!`;
        if (this.meterFillEl) {
          this.meterFillEl.style.width = `${percent}%`;
          this.meterFillEl.style.background = 'var(--luv-red)';
        }
      }
    }
  };

  /* ------------------------------------------------------------------------
     9. FAQ ACCORDION INTERACTION
     ------------------------------------------------------------------------ */
  function initFaq() {
    document.querySelectorAll('.luv-faq-item').forEach(item => {
      const trigger = item.querySelector('.luv-faq-trigger');
      if (trigger) {
        trigger.addEventListener('click', () => {
          const isOpen = item.classList.contains('is-open');
          document.querySelectorAll('.luv-faq-item').forEach(i => i.classList.remove('is-open'));
          if (!isOpen) item.classList.add('is-open');
        });
      }
    });
  }

  /* ------------------------------------------------------------------------
     10. INTERACTIVE KITCHEN ACTIONS (FLIP TEST)
     ------------------------------------------------------------------------ */
  function initInteractiveKitchen() {
    // Interactive Pancake Flip
    const flipBtn = document.getElementById('btn-flip-test');
    const griddleMedia = document.getElementById('griddle-media-wrapper');
    const flipOverlay = document.getElementById('pancake-flip-overlay');

    if (flipBtn && griddleMedia) {
      flipBtn.addEventListener('click', () => {
        griddleMedia.classList.remove('is-flipping');
        void griddleMedia.offsetWidth; // Force reflow
        griddleMedia.classList.add('is-flipping');

        if (flipOverlay) {
          flipOverlay.classList.add('is-active');
          setTimeout(() => {
            flipOverlay.classList.remove('is-active');
            griddleMedia.classList.remove('is-flipping');
          }, 2600);
        }
      });
    }
  }

  /* ------------------------------------------------------------------------
     11. STICKY BUY BAR INTERSECTION OBSERVER (PDP & INDEX)
     ------------------------------------------------------------------------ */
  function initStickyBuyBar() {
    const bar = document.querySelector('.luv-sticky-bar');
    if (!bar) return;

    const trigger = document.getElementById('pdp-buybox-trigger') || document.querySelector('.luv-hero');
    if (!trigger) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
          bar.classList.add('is-visible');
        } else {
          bar.classList.remove('is-visible');
        }
      }, { threshold: 0 });
      observer.observe(trigger);
    }
  }

  /* ------------------------------------------------------------------------
     12. WINKING LOGO MASCOT (PERIODIC & INTERACTIVE)
     ------------------------------------------------------------------------ */
  function initWinkingLogo() {
    const badges = document.querySelectorAll('.luv-winking-badge');
    if (!badges.length) return;

    badges.forEach(badge => {
      let winkTimeout = null;

      function triggerWink(duration = 500) {
        badge.classList.add('is-winking');
        clearTimeout(winkTimeout);
        winkTimeout = setTimeout(() => {
          badge.classList.remove('is-winking');
        }, duration);
      }

      // Automatic periodic wink every 3.6s when visible in viewport
      setInterval(() => {
        const rect = badge.getBoundingClientRect();
        if (rect.top < window.innerHeight + 100 && rect.bottom > -100) {
          triggerWink(450);
        }
      }, 3600);

      // On mouse hover
      badge.addEventListener('mouseenter', () => {
        triggerWink(600);
      });

      // On click / tap
      badge.addEventListener('click', () => {
        triggerWink(700);
      });

      // Keyboard accessibility
      badge.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          triggerWink(700);
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     DOCUMENT READY INIT
     ------------------------------------------------------------------------ */
  document.addEventListener('DOMContentLoaded', () => {
    initLenis();
    initTruthMachine();
    initDuoPackSelector();
    initVideoControls();
    initInteractiveKitchen();
    initFaq();
    initStickyBuyBar();
    initWinkingLogo();
    CartDrawer.init();
  });

})();
